import type { ReactNode } from "react";
import { useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Loader,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  InfoIcon,
  LinkedInIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { ModalInstance } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import { formatDate } from "@js/utils/date";
import { DateFormats } from "@js/utils/date/types";

import {
  useExtendExternalRepostingMutation,
  useGetJobExternalRepostingQuery,
} from "../../api";

import styles from "./styles.module.scss";

export type JobExternalRepostingContentProps = {
  jobId: number;
};

export const JobExternalRepostingContent = ({
  jobId,
}: JobExternalRepostingContentProps) => {
  const [linkedinExtend, setLinkedinExtend] = useState<ExtendTypeValue>(null);
  const [vonqExtend, setVonqExtend] = useState<ExtendTypeValue>(null);
  const { data, isLoading } = useGetJobExternalRepostingQuery({ jobId });
  const [extendExternalReposting, { isLoading: isSaving }] =
    useExtendExternalRepostingMutation();

  const handleRepost = async () => {
    if (isSaving) {
      return;
    }
    try {
      await extendExternalReposting({
        jobId,
        extend_linkedin_promotion: !!linkedinExtend ? true : undefined,
        extend_vonq_promotion: !!vonqExtend ? true : undefined,
        automatic_vonq_posting_enabled:
          vonqExtend === EXTEND_TYPE.FORCE ? true : undefined,
        automatic_linkedin_posting_enabled:
          linkedinExtend === EXTEND_TYPE.FORCE ? true : undefined,
      }).unwrap();
      ModalInstance.close();
      Snackbar.success("Job was successfully re-posted.");
    } catch (_error) {
      Snackbar.error("Failed to re-post the job.");
    }
  };

  if (!data || isLoading) {
    return <Loader centered />;
  }

  const disableCTA = !linkedinExtend && !vonqExtend;

  return (
    <Stack sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography
        component="h2"
        size="small"
        variant="title"
        fontWeight={400}
        sx={{ mb: 0.5 }}
      >
        Job External Promotion
      </Typography>

      <Typography
        component="p"
        size="medium"
        fontWeight={400}
        sx={{ mb: 2, color: "var(--grey-1)" }}
      >
        Select services you want to extend.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <ExtendPostingSection
          cannotBeExtendedReason={data.linkedin_cannot_be_posted_reason}
          isEligible={data.is_eligible_for_linkedin_posting}
          isRepostingForced={!!data.automatic_linkedin_posting_enabled}
          promotionExpiresAt={data.linkedin_promotion_expires_at}
          value={linkedinExtend}
          onChange={(value) => setLinkedinExtend(value)}
          icon={
            <LinkedInIcon
              sx={{
                color: "#0a66c2",
                verticalAlign: "sub",
                fontSize: "26px",
                mr: 0.25,
                mb: "-1px",
              }}
            />
          }
          label={"LinkedIn"}
        />
      </Box>
      <Divider color="beige" sx={{ mb: 2 }} />
      <Box>
        <ExtendPostingSection
          cannotBeExtendedReason={data.vonq_cannot_be_posted_reason}
          isEligible={data.is_eligible_for_vonq_posting}
          isRepostingForced={!!data.automatic_vonq_posting_enabled}
          promotionExpiresAt={data.vonq_promotion_expires_at}
          value={vonqExtend}
          onChange={(value) => setVonqExtend(value)}
          icon={
            <Box
              sx={{
                width: "19px",
                height: "19px",
                borderRadius: "3px",
                backgroundColor: "#B92554",
                display: "inline-block",
                position: "relative",
                mb: "-3px",
                mr: 0.5,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "var(--white)",
                  fontWeight: "500",
                  fontSize: "15px",
                }}
              >
                Q
              </Box>
            </Box>
          }
          label="VonQ"
        />
      </Box>
      <Stack
        direction="row"
        sx={{ gap: 1, justifyContent: "end", mt: { xs: 3, sm: 4 } }}
      >
        <Button variant="transparent" onClick={ModalInstance.close}>
          Cancel
        </Button>

        <Button
          variant="primary"
          disabled={disableCTA}
          onClick={() => {
            void handleRepost();
          }}
          sx={{ pointerEvents: isSaving ? "none" : undefined }}
          endIcon={isSaving ? <Loader size={20} color="info" /> : undefined}
        >
          Extend
        </Button>
      </Stack>
    </Stack>
  );
};

export const JobRepostingDetails = ({
  cannotBeExtendedReason,
  isEligible,
  promotionExpiresAt,
  isRepostingForced,
  label,
}: {
  label: string;
  isEligible: boolean;
  cannotBeExtendedReason: string | null;
  promotionExpiresAt: string;
  isRepostingForced: boolean;
}) => {
  if (!isEligible || cannotBeExtendedReason) {
    return (
      <>
        <Typography component="p" size="small">
          <InfoIcon sx={{ fontSize: "inherit", mb: "-2px" }} /> This job isn’t
          eligible to be reposted on {label}.
          {!!cannotBeExtendedReason ? (
            <strong> {cannotBeExtendedReason}</strong>
          ) : (
            <> Do you want to force {label} promotion for this job?</>
          )}
        </Typography>
      </>
    );
  }

  const repostedUntilDate = formatDate(
    promotionExpiresAt,
    DateFormats["Jan 1, 1970"],
  );
  if (isRepostingForced) {
    return (
      <>
        <Typography component="p" size="small">
          <InfoIcon sx={{ fontSize: "inherit", mb: "-2px" }} /> This job is
          currently being reposted on {label} until {repostedUntilDate}. Do you
          want to extend the {label} promotion for this job?
        </Typography>
      </>
    );
  }

  return (
    <>
      <Typography component="p" size="small">
        <InfoIcon sx={{ fontSize: "inherit", mb: "-2px" }} /> This job will be
        reposted on {label} while it meets the required criteria, up to{" "}
        {repostedUntilDate}. Do you want to extend the {label} promotion for
        this job?
      </Typography>
    </>
  );
};

const EXTEND_TYPE = {
  FORCE: "force",
  EXTEND: "extend",
} as const;

type ExtendTypeValue = null | ObjectValues<typeof EXTEND_TYPE>;

type ExtendPostingSectionProps = {
  cannotBeExtendedReason: string | null;
  value: ExtendTypeValue;
  onChange: (value: ExtendTypeValue) => void;
  label: string;
  icon: ReactNode;
  isRepostingForced: boolean;
  isEligible: boolean;
  promotionExpiresAt: string;
};

const ExtendPostingSection = ({
  isEligible,
  isRepostingForced,
  cannotBeExtendedReason,
  value,
  onChange,
  label,
  icon,
  promotionExpiresAt,
}: ExtendPostingSectionProps) => {
  const cannotBeReposted = !!cannotBeExtendedReason;
  const canForce = !cannotBeReposted && !isRepostingForced;
  const canExtendAndForce = canForce && isEligible;
  return (
    <Stack>
      <Checkbox
        label={
          <Typography
            component="span"
            size="large"
            sx={{ color: "var(--black)", lineHeight: "1.4 !important" }}
          >
            {icon} Extend {label} job promotion
          </Typography>
        }
        disabled={!!cannotBeExtendedReason}
        checked={!!value}
        onChange={(_e, checked) => {
          const transformedValue = checked ? EXTEND_TYPE.EXTEND : null;
          onChange(transformedValue);
        }}
      />
      <JobRepostingDetails
        isEligible={isEligible}
        isRepostingForced={isRepostingForced}
        cannotBeExtendedReason={cannotBeExtendedReason}
        promotionExpiresAt={promotionExpiresAt}
        label={label}
      />
      {canExtendAndForce && !!value && (
        <RadioGroup
          sx={{ mt: 1 }}
          value={value}
          onChange={(event) => {
            onChange(event.target.value as ExtendTypeValue);
          }}
        >
          <FormControlLabel
            value={EXTEND_TYPE.EXTEND}
            control={<Radio />}
            label="Extend promotion"
          />
          <FormControlLabel
            value={EXTEND_TYPE.FORCE}
            control={<Radio />}
            label="Extend and force promotion"
          />
        </RadioGroup>
      )}
    </Stack>
  );
};

export const openJobExternalRepostingModal = (jobId: number) => {
  ModalInstance.open({
    children: <JobExternalRepostingContent jobId={jobId} />,
    className: styles.modal,
    padding: false,
    closeButton: true,
    closeButtonProps: {
      variant: "tertiary",
      size: "small",
      className: styles.closeButton,
    },
  });
};
